import { useMemo } from 'react';

interface StockStats {
  last: number;
  open: number;
  high: number;
  low: number;
  tFreq: number;
  tVol: number;
  tVal: number; // in IDR
  wap: number;
  prevClose: number | null;
}

function computeStats(allTrades: any[], currentTime: number): Omit<StockStats, 'prevClose'> {
  const empty = { last: 0, open: 0, high: 0, low: 0, tFreq: 0, tVol: 0, tVal: 0, wap: 0 };
  if (!allTrades.length) return empty;

  let last = 0, open = 0, high = -Infinity, low = Infinity;
  let tVol = 0, tVal = 0, tFreq = 0;
  let firstSet = false;

  // NOTE: allTrades harus done trades (1 row = 1 transaksi, single-counted).
  // Jangan pakai synthetic order events (1 trade = 2 event buy+sell) karena
  // Lot/Value jadi double-count. Lihat OrderbookReplay filteredDoneOrders.
  for (const o of allTrades) {
    if (o.timeValue > currentTime) break;

    if (!o.lot || o.lot <= 0) continue;
    // Skip orderbook-only states kalau caller masih passing synthetic events.
    const msg = o.message;
    if (msg === 'Open' || msg === 'Withdrawn' || msg === 'Expired' || msg === 'Amended') continue;

    const price = o.price;
    const vol = o.lot;

    if (!firstSet) { open = price; firstSet = true; }
    if (price > high) high = price;
    if (price < low) low = price;
    last = price;
    tVol += vol;
    tVal += price * vol * 100;
    tFreq += 1;
  }

  // WAP = trade VWAP (bukan rata-rata sisa open order).
  // Konsisten dengan Avg di Broker Summary: TVal / (TLot * 100).
  const wap = tVol > 0 ? Math.round(tVal / (tVol * 100)) : 0;

  if (!firstSet) return empty;
  return { last, open, high, low: low === Infinity ? 0 : low, tFreq, tVol, tVal, wap };
}

function fmt(n: number, dec = 0) {
  if (!n && n !== 0) return '—';
  return n.toLocaleString('id-ID', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function fmtBillion(val: number) {
  if (!val) return '—';
  const b = val / 1e9;
  return b >= 1000 ? `${(b / 1000).toFixed(2)} T` : `${b.toFixed(2)} B`;
}

interface Props {
  // Single-counted done trades (filteredDoneOrders). Bukan synthetic order events.
  allOrders: any[];
  currentTime: number;
  prevClose: number | null;
  stock?: string;
}

export function StockInfoHeader({ allOrders, currentTime, prevClose, stock }: Props) {
  const s = useMemo(() => computeStats(allOrders, currentTime), [allOrders, currentTime]);

  const chg = (prevClose && s.last) ? s.last - prevClose : null;
  const chgPct = (prevClose && chg !== null) ? (chg / prevClose) * 100 : null;

  const chgColor = chg === null ? 'text-gray-400'
    : chg > 0 ? 'text-green-400'
    : chg < 0 ? 'text-red-400'
    : 'text-yellow-300';

  const lastColor = prevClose && s.last ? 
    (s.last > prevClose ? 'text-green-400' : s.last < prevClose ? 'text-red-400' : 'text-yellow-300') 
    : 'text-yellow-300';

  const openColor = prevClose && s.open ? 
    (s.open > prevClose ? 'text-green-400' : s.open < prevClose ? 'text-red-400' : 'text-yellow-300') 
    : 'text-yellow-300';

  const trend = chg === null || !s.last ? null : chg > 0 ? '▲' : chg < 0 ? '▼' : null;
  const trendColor = chg !== null && chg > 0 ? 'text-green-400' : chg < 0 ? 'text-red-400' : 'text-yellow-300';

  const lbl = (text: string) => <span className="text-white text-[14.5px] shrink-0">{text}</span>;
  const val = (text: string, cls = 'text-white') => <span className={`text-[14.5px] font-bold whitespace-nowrap ml-auto text-right ${cls}`}>{text}</span>;

  return (
    <div className="w-full bg-gray-900 border-b border-gray-700 px-1.5 py-1">
      {/* Row 1: Ticker box + Last */}
      <div className="flex items-center gap-2 mb-1">
        <div className="border border-gray-500 px-2 py-0.5 text-white font-bold text-[14.5px] whitespace-nowrap min-w-[44px] text-center">
          {stock ?? '—'}
        </div>
        <div className="flex items-center gap-1">
          {lbl('Last')}
          {val(s.last ? fmt(s.last) : '—', lastColor)}
          {trend && <span className={`text-[14.5px] font-bold ${trendColor}`}>{trend}</span>}
        </div>
      </div>
      {/* Rows 2-4: 3-column grid with dividers */}
      <div className="grid grid-cols-3 divide-x divide-[#2d3748]">
        <div className="flex flex-col gap-[2px] pr-2">
          <div className="flex gap-1 items-center">
            {lbl('Previous')}
            {val(prevClose ? fmt(prevClose) : '—', 'text-yellow-300')}
          </div>
          <div className="flex gap-1 items-center">{lbl('Change')}{val(chg !== null ? (chg > 0 ? `+${fmt(chg)}` : fmt(chg)) : '—', chgColor)}</div>
          <div className="flex gap-1 items-center">{lbl('%')}{val(chgPct !== null ? (chgPct > 0 ? `+${chgPct.toFixed(1)}` : chgPct.toFixed(1)) : '—', chgColor)}</div>
        </div>
        <div className="flex flex-col gap-[2px] px-2">
          <div className="flex gap-1 items-center">{lbl('Open')}{val(s.open ? fmt(s.open) : '—', openColor)}</div>
          <div className="flex gap-1 items-center">{lbl('High')}{val(s.high ? fmt(s.high) : '—', 'text-green-400')}</div>
          <div className="flex gap-1 items-center">{lbl('Low')}{val(s.low ? fmt(s.low) : '—', 'text-red-400')}</div>
        </div>
        <div className="flex flex-col gap-[2px] pl-2">
          <div className="flex gap-1 items-center">{lbl('Lot')}{val(fmt(s.tVol))}</div>
          <div className="flex gap-1 items-center">{lbl('Value')}{val(fmtBillion(s.tVal))}</div>
          <div className="flex gap-1 items-center">{lbl('WAP')}{val(s.wap ? fmt(s.wap) : '—')}</div>
        </div>
      </div>
    </div>
  );
}

